// ✅ ContractorListing.jsx (UPDATED WITH LOCALSTORAGE HELPER + PARSED VALUES)

import React, { useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaPlus,
  FaSearch,
  FaChevronUp,
  FaChevronDown,
  FaTrash,
  FaEdit
} from "react-icons/fa";
import PaymentLayoutWrapper from "../../components/PaymentLayoutWrapper";
import { useNavigate } from "react-router-dom";

const BASE_URL = "https://nissi-india.com"; // ✅ change if needed

const ContractorListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  // ✅ localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");
  const branchIdLS = getLS("branchId") || getLS("branchid") || "8";
  const branchTypeLS = getLS("branchType") || getLS("branchtype") || "HO";

  // ✅ parsed values
  const userId = useMemo(() => String(userIdLS || "").trim(), [userIdLS]);
  const branchId = useMemo(() => Number(branchIdLS || 8), [branchIdLS]);
  const branchType = useMemo(
    () => String(branchTypeLS || "HO").trim(),
    [branchTypeLS]
  );

  // Dropdown options
  const [nameOptions, setNameOptions] = useState([]); // [{id,name}]
  const [codeOptions, setCodeOptions] = useState([]); // [{id,code}]

  // Search state
  const [searchData, setSearchData] = useState({
    contractorName: "",
    contractorCode: ""
  });

  // Listing data
  const [contractorData, setContractorData] = useState([]);

  // ✅ Selection (checkbox)
  const [selectedIds, setSelectedIds] = useState([]);

  const selectedCount = selectedIds.length;
  const selectedSingleId = selectedCount === 1 ? selectedIds[0] : null;

  const handleSearchChange = (field, value) => {
    setSearchData((prev) => ({ ...prev, [field]: value }));
  };

  const clearSelection = () => setSelectedIds([]);

  // ✅ Load dropdowns
  useEffect(() => {
    if (!branchId) {
      toast.error("branchId not found. Please login again.");
      return;
    }

    const loadDropdowns = async () => {
      try {
        const [namesRes, codesRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/contractors/names`, {
            params: { branchid: branchId, branchType, userid: userId }
          }),
          axios.get(`${BASE_URL}/api/contractors/codes`, {
            params: { branchid: branchId, branchType, userid: userId }
          })
        ]);

        setNameOptions(namesRes?.data?.data || []);
        setCodeOptions(codesRes?.data?.data || []);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load contractor dropdowns");
      }
    };

    loadDropdowns();
  }, [branchId, branchType, userId]);

  // ✅ Load list (default full list)
  useEffect(() => {
    if (!branchId) return;
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  const handleSearch = async () => {
    if (!branchId) return toast.error("branchId missing");

    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/contractors/list`, {
        params: {
          branchid: branchId,
          branchType,
          userid: userId,
          code: searchData.contractorCode || "",
          name: searchData.contractorName || ""
        }
      });

      const rows = res?.data?.data || [];
      setContractorData(rows);
      clearSelection();
      toast.success("Search completed");
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch contractor list");
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    navigate("/PaymentVouchers/ContractorEntry", {
      state: {
        mode: "add",
        branchId,
        branchType,
        userId
      }
    });
  };

  const handleClear = () => {
    setSearchData({ contractorName: "", contractorCode: "" });
    setTimeout(() => handleSearch(), 0);
  };

  // ✅ Checkbox handlers
  const toggleRow = (id) => {
    if (id === null || id === undefined) return;
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const allRowIds = contractorData
    .map((r) => r.contractorMasId)
    .filter((x) => x !== null && x !== undefined);

  const isAllSelected =
    allRowIds.length > 0 && allRowIds.every((id) => selectedIds.includes(id));

  const toggleSelectAll = () => {
    if (isAllSelected) setSelectedIds([]);
    else setSelectedIds(allRowIds);
  };

  // ✅ Delete selected
  const handleDeleteSelected = async () => {
    if (!branchId) return toast.error("branchId missing");
    if (selectedIds.length === 0) return;

    const ok = window.confirm(`Delete ${selectedIds.length} selected contractor(s)?`);
    if (!ok) return;

    try {
      setLoading(true);

      const res = await axios.post(`${BASE_URL}/api/contractors/delete`, {
        branchid: branchId,
        branchType,
        userid: userId,
        ids: selectedIds
      });

      if (res?.data?.success) {
        toast.success("Deleted successfully");
        await handleSearch();
      } else {
        toast.error(res?.data?.message || "Delete failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Delete failed");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Update (single selected) -> getById -> redirect with state
  const handleUpdateSelected = async () => {
    if (!branchId) return toast.error("branchId missing");
    if (!selectedSingleId) {
      return toast.info("Select exactly one row to update");
    }

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/contractors/getById`, {
        params: {
          branchid: branchId,
          branchType,
          userid: userId,
          contractorid: selectedSingleId
        }
      });

      const row = res?.data?.data;
      if (!res?.data?.success || !row) {
        toast.error(res?.data?.message || "Failed to load contractor");
        return;
      }

      navigate("/PaymentVouchers/ContractorEntry", {
        state: {
          mode: "edit",
          contractor: row,
          branchId,
          branchType,
          userId
        }
      });
    } catch (err) {
      console.error(err);
      toast.error("Failed to open update");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PaymentLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">CONTRACTOR LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-3">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Contractor Listing
            </h1>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
              }`}
            >
              {showFilters ? <FaChevronUp /> : <FaChevronDown />}
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
          </div>

          {selectedCount > 0 && (
            <div
              className={`mb-4 p-3 rounded-xl border flex flex-wrap items-center justify-between gap-3 ${
                isDark ? "border-white/10 bg-white/10" : "border-gray-200 bg-gray-50"
              }`}
            >
              <div className={`${isDark ? "text-white" : "text-gray-800"} font-semibold`}>
                Selected: {selectedCount}
                {selectedSingleId ? ` (ID: ${selectedSingleId})` : ""}
              </div>

              <div className="flex flex-wrap gap-3">
                {selectedSingleId && (
                  <button
                    onClick={handleUpdateSelected}
                    disabled={loading}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all ${
                      loading ? "opacity-70 cursor-not-allowed" : ""
                    } ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaEdit />
                    Update
                  </button>
                )}

                <button
                  onClick={handleDeleteSelected}
                  disabled={loading}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all ${
                    loading ? "opacity-70 cursor-not-allowed" : ""
                  } bg-red-600 hover:bg-red-700 text-white`}
                >
                  <FaTrash />
                  Delete
                </button>

                <button
                  onClick={clearSelection}
                  className={`px-4 py-2 rounded-lg font-semibold border ${
                    isDark
                      ? "border-gray-600 bg-gray-700 hover:bg-gray-600 text-white"
                      : "border-gray-300 bg-white hover:bg-gray-100 text-gray-700"
                  }`}
                >
                  Clear Selection
                </button>
              </div>
            </div>
          )}

          {showFilters && (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Contractor Name
                  </label>
                  <select
                    value={searchData.contractorName}
                    onChange={(e) => handleSearchChange("contractorName", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {nameOptions.map((x, idx) => (
                      <option key={x.id ?? idx} value={x.name}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Contractor Code
                  </label>
                  <select
                    value={searchData.contractorCode}
                    onChange={(e) => handleSearchChange("contractorCode", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {codeOptions.map((x, idx) => (
                      <option key={x.id ?? idx} value={x.code}>
                        {x.code}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex flex-wrap justify-center items-center gap-4 mb-6">
                <button
                  onClick={handleAddNew}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaPlus />
                  Add New
                </button>

                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                    loading ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  <FaSearch />
                  {loading ? "Searching..." : "Search"}
                </button>

                <button
                  onClick={handleClear}
                  className={`px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    isDark
                      ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                  }`}
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-3 text-center font-semibold border-r border-white/20">
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 cursor-pointer"
                      />
                    </th>

                    <th className="px-4 py-3 text-center font-semibold border-r border-white/20">Sl.No</th>
                    <th className="px-4 py-3 text-center font-semibold border-r border-white/20">Contractor Id</th>
                    <th className="px-4 py-3 text-center font-semibold border-r border-white/20">Contractor Name</th>
                    <th className="px-4 py-3 text-center font-semibold border-r border-white/20">Contact Number</th>
                    <th className="px-4 py-3 text-center font-semibold border-r border-white/20">Address</th>
                    <th className="px-4 py-3 text-center font-semibold">Branch Name</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className={`px-4 py-6 text-center ${isDark ? "text-gray-200" : "text-gray-600"}`}>
                        Loading...
                      </td>
                    </tr>
                  ) : contractorData.length === 0 ? (
                    <tr>
                      <td colSpan={7} className={`px-4 py-6 text-center ${isDark ? "text-gray-200" : "text-gray-600"}`}>
                        No data found
                      </td>
                    </tr>
                  ) : (
                    contractorData.map((c, idx) => {
                      const id = c.contractorMasId;
                      const checked = selectedIds.includes(id);

                      return (
                        <tr
                          key={`${id || idx}-${idx}`}
                          className={`transition-colors duration-200 ${
                            isDark ? "bg-gray-800 hover:bg-gray-750" : "bg-white hover:bg-gray-50"
                          } ${checked ? (isDark ? "ring-1 ring-white/20" : "ring-1 ring-gray-300") : ""}`}
                        >
                          <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleRow(id)}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>

                          <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                            {c.slNo ?? idx + 1}
                          </td>
                          <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-blue-400" : "border-gray-200 text-blue-600"}`}>
                            {c.contractorId}
                          </td>
                          <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                            {c.contractorName}
                          </td>
                          <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                            {c.contactNumber}
                          </td>
                          <td className={`px-4 py-3 text-left border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                            {c.address}
                          </td>
                          <td className={`px-4 py-3 text-center ${isDark ? "text-white" : "text-gray-900"}`}>
                            {c.branchName}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </PaymentLayoutWrapper>
  );
};

export default ContractorListing;